/**
 * Sign in with a texted code — docs/phone-login.md.
 *
 * Only works for accounts that verified a phone *and* turned on phone sign-in
 * in Settings. "Send code" fails with the reason (no account / turned off),
 * shown under the number field.
 */
import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import type { CountryCode } from "libphonenumber-js/max";
import AuthLayout from "@/components/layout/AuthLayout";
import PhoneField from "@/components/forms/PhoneField";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/lib/auth";
import { DEFAULT_COUNTRY, formatInternational, toE164 } from "@/lib/phone";
import { apiErrorMessage, isApiErrorHandled } from "@/services/api";
import { phoneLoginStartService, phoneLoginVerifyService } from "@/services/auth.service";
import { phoneLoginSchema, type PhoneLoginValues } from "@/schemas/auth";
import { PUBLIC_ROUTES } from "@/routes/url";

const RESEND_COOLDOWN_S = 30;

export default function PhoneLogin() {
  const { user, context, installSession } = useAuth();
  const navigate = useNavigate();
  // E.164, set once a code has been requested — that is what moves to step two.
  const [phone, setPhone] = useState<string | null>(null);
  const [sentMessage, setSentMessage] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const form = useForm<PhoneLoginValues>({
    resolver: zodResolver(phoneLoginSchema),
    defaultValues: { phone: "", phoneCountry: DEFAULT_COUNTRY },
  });

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const showError = (err: unknown) => {
    if (!isApiErrorHandled(err)) toast.error(apiErrorMessage(err));
  };

  const send = async (e164: string) => {
    setBusy(true);
    try {
      const res = await phoneLoginStartService(e164);
      setSentMessage(res.message);
      setPhone(e164);
      setCooldown(RESEND_COOLDOWN_S);
    } catch (err) {
      // On step one the reason belongs under the number; a resend toasts.
      if (!phone && !isApiErrorHandled(err)) form.setError("phone", { message: apiErrorMessage(err) });
      else showError(err);
    } finally {
      setBusy(false);
    }
  };

  const signIn = async () => {
    if (!phone) return;
    setBusy(true);
    try {
      const { data, message } = await phoneLoginVerifyService({ phone, code });
      if (!data) throw new Error(message);
      await installSession(data.token, data.user);
      toast.success("Welcome back");
      navigate(PUBLIC_ROUTES.OVERVIEW, { replace: true });
    } catch (err) {
      // Cleared: a rejected code left in the box invites re-submitting it.
      setCode("");
      showError(err);
    } finally {
      setBusy(false);
    }
  };

  if (user) {
    return <Navigate to={context?.landing_route || PUBLIC_ROUTES.OVERVIEW} replace />;
  }

  return (
    <AuthLayout>
      {!phone ? (
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(({ phone: raw, phoneCountry }) =>
              send(toE164(raw, (phoneCountry || DEFAULT_COUNTRY) as CountryCode) ?? raw),
            )}
            data-testid="phone-login-form"
            noValidate
          >
            <h1 className="font-display text-4xl tracking-tight text-center">Sign in with a code.</h1>
            <p className="text-stone mt-3 text-sm text-center">
              We&apos;ll text a 6-digit code to the mobile number on your account.
            </p>
            <div className="mt-8">
              <PhoneField name="phone" countryName="phoneCountry" label="Mobile number" testId="phone-login-phone" showResolved={false} required />
            </div>
            <Button type="submit" variant="dashboardPrimary" size="lg" loading={busy} className="mt-8 w-full" data-testid="phone-login-send">
              Send code
            </Button>
          </form>
        </Form>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); void signIn(); }} data-testid="phone-login-code-form" noValidate>
          <h1 className="font-display text-4xl tracking-tight text-center">Enter your code.</h1>
          <p className="text-stone mt-3 text-sm leading-relaxed text-center" data-testid="phone-login-sent">
            {sentMessage} <span className="text-ink">{formatInternational(phone)}</span>
          </p>
          <div className="mt-8 flex flex-col gap-1.5">
            <label htmlFor="phone-login-code" className="text-sm font-medium">Verification code</label>
            <Input
              id="phone-login-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="6-digit code"
              autoFocus
              data-testid="phone-login-code"
            />
            <div className="flex justify-between text-xs">
              <button
                type="button"
                className="text-stone hover:text-moss underline underline-offset-2 disabled:no-underline disabled:opacity-60"
                onClick={() => void send(phone)}
                disabled={busy || cooldown > 0}
                data-testid="phone-login-resend"
              >
                {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
              </button>
              <button
                type="button"
                className="text-stone hover:text-moss underline underline-offset-2"
                onClick={() => { setPhone(null); setCode(""); }}
                data-testid="phone-login-change-number"
              >
                Use a different number
              </button>
            </div>
          </div>
          <Button type="submit" variant="dashboardPrimary" size="lg" loading={busy} disabled={code.length !== 6} className="mt-8 w-full" data-testid="phone-login-submit">
            Sign in
          </Button>
          <p className="mt-6 text-xs text-stone leading-relaxed text-center" data-testid="phone-login-hint">
            No code? If you replied STOP to our texts, reply START, then try again.
          </p>
        </form>
      )}
      <div className="my-5 flex items-center gap-3 text-xs uppercase tracking-widest text-stone" aria-hidden="true">
        <span className="h-px flex-1 bg-edge" />or<span className="h-px flex-1 bg-edge" />
      </div>
      <Button asChild variant="dashboardOutline" size="lg" className="w-full border-primary text-primary hover:text-primary">
        <Link to={PUBLIC_ROUTES.LOGIN} data-testid="phone-login-use-email">Sign in with email</Link>
      </Button>
    </AuthLayout>
  );
}
